import { useState, useEffect } from "react";
import { fetchSlackSettings, updateSlackSettings, testSlackWebhook, triggerDueDateCheck } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { ArrowLeft, Check, AlertCircle, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function AdminSlackSettings({ users }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [channel, setChannel] = useState("#doit-updates");
  const [enabled, setEnabled] = useState(false);
  const [notifyStatusChange, setNotifyStatusChange] = useState(true);
  const [notifyAssignment, setNotifyAssignment] = useState(true);
  const [notifyMention, setNotifyMention] = useState(true);
  const [notifyDueDate, setNotifyDueDate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSlackSettings().then((s) => {
      setSettings(s);
      setBotToken(s.bot_token || "");
      setWebhookUrl(s.webhook_url || "");
      setChannel(s.channel || "#doit-updates");
      setEnabled(s.enabled || false);
      setNotifyStatusChange(s.notify_status_change !== false);
      setNotifyAssignment(s.notify_assignment !== false);
      setNotifyMention(s.notify_mention !== false);
      setNotifyDueDate(s.notify_due_date !== false);
    }).catch(console.error);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await updateSlackSettings({
        bot_token: botToken,
        webhook_url: webhookUrl,
        channel,
        enabled,
        notify_status_change: notifyStatusChange,
        notify_assignment: notifyAssignment,
        notify_mention: notifyMention,
        notify_due_date: notifyDueDate,
        user_mappings: settings?.user_mappings || [],
      });
      setSettings(updated);
      toast.success("Slack settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!botToken && !webhookUrl) {
      toast.error("Enter a bot token or webhook URL");
      return;
    }
    try {
      setTesting(true);
      setTestResult(null);
      const result = await testSlackWebhook(botToken, webhookUrl, channel);
      setTestResult(result);
      if (result.success) {
        toast.success("Test message sent to Slack");
      } else {
        toast.error(result.message || "Test failed");
      }
    } catch (e) {
      setTestResult({ success: false, message: "Connection error" });
      toast.error("Failed to test connection");
    } finally {
      setTesting(false);
    }
  };

  const handleDueDateCheck = async () => {
    try {
      const result = await triggerDueDateCheck();
      toast.success(result.message + ` (${result.notified} notified)`);
    } catch (e) {
      toast.error("Failed to run due date check");
    }
  };

  return (
    <div className="h-full overflow-hidden" data-testid="admin-slack-settings">
      <ScrollArea className="h-full">
        <div className="max-w-2xl mx-auto p-8 space-y-8">
          {/* Header */}
          <div className="flex items-center gap-3">
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate("/dashboard")}
              className="p-1.5 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div>
              <h1
                className="text-2xl font-black tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Slack Integration
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Configure Slack notifications for your team
              </p>
            </div>
          </div>

          {/* Bot Token + Webhook URL */}
          <div className="space-y-3 p-5 border border-gray-200 rounded-sm bg-white">
            <h3
              className="text-sm font-bold"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Connection
            </h3>
            <p className="text-xs text-gray-500">
              Configure either a Bot Token or Webhook URL. Bot Token is preferred — it uses Slack's chat.postMessage API directly. If both are set, Bot Token takes priority.
            </p>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Slack Bot Token
              </label>
              <input
                data-testid="slack-bot-token-input"
                type="password"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder="xoxb-..."
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all font-mono"
              />
              <p className="text-[10px] text-gray-400">
                Create a Slack App at api.slack.com/apps, add chat:write scope, install to workspace, copy Bot User OAuth Token
              </p>
            </div>

            <div className="relative flex items-center my-3">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-3 text-[10px] font-bold uppercase text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Webhook URL
              </label>
              <input
                data-testid="slack-webhook-url-input"
                type="text"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all font-mono"
              />
              <p className="text-[10px] text-gray-400">
                Create an Incoming Webhook at api.slack.com/apps
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Default Channel
              </label>
              <input
                data-testid="slack-channel-input"
                type="text"
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="#doit-updates"
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
              />
              <p className="text-[10px] text-gray-400">
                For Bot Token: the bot must be invited to this channel. For Webhook: messages go to the webhook's configured channel.
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                data-testid="slack-test-btn"
                onClick={handleTest}
                disabled={testing || (!botToken && !webhookUrl)}
                className="bg-white text-black border border-gray-300 hover:border-black rounded-sm px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40 flex items-center gap-2"
              >
                <Send className="w-3.5 h-3.5" />
                {testing ? "Sending..." : "Test Connection"}
              </button>
              {testResult && (
                <span className={`flex items-center gap-1.5 text-xs font-medium ${testResult.success ? "text-green-600" : "text-red-600"}`}>
                  {testResult.success ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                  {testResult.success ? "Connected" : testResult.message}
                </span>
              )}
            </div>
          </div>

          {/* Enable/Disable */}
          <div className="space-y-3 p-5 border border-gray-200 rounded-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
                  Enable Notifications
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Turn on/off all Slack notifications
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  data-testid="slack-enabled-toggle"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  className="rounded-sm"
                />
                <span className={`text-sm font-semibold ${enabled ? "text-green-600" : "text-gray-400"}`}>
                  {enabled ? "Active" : "Disabled"}
                </span>
              </label>
            </div>
          </div>

          {/* Notification Types */}
          <div className="space-y-3 p-5 border border-gray-200 rounded-sm bg-white">
            <h3 className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
              Notification Types
            </h3>
            <div className="space-y-3">
              {[
                { key: "status", label: "Status Changes", desc: "When an issue status changes (e.g., Backlog -> In Progress)", checked: notifyStatusChange, set: setNotifyStatusChange, testId: "notify-status-change" },
                { key: "assign", label: "Issue Assignments", desc: "When an issue is assigned to someone", checked: notifyAssignment, set: setNotifyAssignment, testId: "notify-assignment" },
                { key: "mention", label: "@Mention Notifications", desc: "When a user is @mentioned in a comment", checked: notifyMention, set: setNotifyMention, testId: "notify-mention" },
                { key: "due", label: "Due Date Reminders", desc: "1 day before an issue is due", checked: notifyDueDate, set: setNotifyDueDate, testId: "notify-due-date" },
              ].map((item) => (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 border border-gray-100 rounded-sm hover:border-gray-300 cursor-pointer transition-colors"
                >
                  <Checkbox
                    data-testid={item.testId}
                    checked={item.checked}
                    onCheckedChange={item.set}
                    className="rounded-sm mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium text-black">{item.label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pb-8">
            <button
              data-testid="slack-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-brand text-white hover:bg-brand-dark rounded-sm px-6 py-2.5 text-sm font-semibold transition-colors disabled:opacity-40"
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
            <button
              data-testid="slack-due-date-check-btn"
              onClick={handleDueDateCheck}
              className="bg-white text-black border border-gray-300 hover:border-black rounded-sm px-4 py-2.5 text-sm font-semibold transition-colors"
            >
              Run Due Date Check
            </button>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
